import { useId, type ReactNode } from 'react'
import { useApp } from '@/app/AppContext'
import '@/styles/record.css'

/**
 * Вкладки карточки (`.tabs` дизайн-системы) — не больше четырёх-пяти.
 *
 * Счётчик — рядом с названием (`count-pill`): «Позиции 4» отвечает на вопрос, стоит ли
 * открывать вкладку, без её открытия. Стрелки влево-вправо ходят по вкладкам — так ведёт
 * себя tablist для экранного диктора и клавиатуры.
 */
export interface RecordTab {
  id: string
  label: ReactNode
  count?: number | null
}

export interface RecordTabsProps {
  tabs: RecordTab[]
  active: string
  onChange: (id: string) => void
  ariaLabel?: string
  children: ReactNode
}

export function RecordTabs({ tabs, active, onChange, ariaLabel, children }: RecordTabsProps) {
  const { t } = useApp()
  const base = useId()
  const move = (from: number, step: number) => {
    const next = tabs[(from + step + tabs.length) % tabs.length]
    if (!next) return
    onChange(next.id)
    document.getElementById(`${base}-tab-${next.id}`)?.focus()
  }
  return (
    <>
      <div className="tabs" role="tablist" aria-label={ariaLabel ?? t('record.tabs')}>
        {tabs.map((tab, index) => (
          <button
            key={tab.id}
            id={`${base}-tab-${tab.id}`}
            type="button"
            role="tab"
            className="tab"
            aria-selected={tab.id === active}
            aria-controls={`${base}-panel`}
            tabIndex={tab.id === active ? 0 : -1}
            onClick={() => onChange(tab.id)}
            onKeyDown={(event) => {
              if (event.key === 'ArrowRight') move(index, 1)
              if (event.key === 'ArrowLeft') move(index, -1)
            }}
          >
            {tab.label}
            {tab.count !== undefined && tab.count !== null && <span className="count-pill">{tab.count}</span>}
          </button>
        ))}
      </div>
      <div id={`${base}-panel`} role="tabpanel" aria-labelledby={`${base}-tab-${active}`}>
        {children}
      </div>
    </>
  )
}
